/**
 * CommunityRequestCard Component
 * Displays a ritual request sent to a community for co-organization
 * Used in the Requests subtab of AdminRitualTab
 */

import { MessageCircleMore } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Card } from "@/components/ui/card"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useCreateDmConversation } from "@/features/chats/hooks/useDmChat"
import {
  useAcceptOrganizerInvite,
  useRejectOrganizerInvite,
} from "@/features/rituals/hooks/useRitualInvites"
import { cn } from "@/lib"

export interface CommunityRequestCardProps {
  id: string
  organizerRecordId: string
  image?: string
  title: string
  type: string
  price?: number
  participationFee?: "Free" | "Paid" | "Donation-based"
  dateTime?: string
  duration: string
  location?: string
  chatCount?: number
  ritualCreatorId: string
  inviterName: string
  inviterUsername?: string
  inviterAvatar?: string
  inviteMessage: string
  inviteNote?: string
}

export function CommunityRequestCard({
  id,
  organizerRecordId,
  image,
  title,
  type,
  price,
  participationFee,
  dateTime,
  duration,
  location,
  chatCount = 0,
  ritualCreatorId,
  inviterName,
  inviterUsername,
  inviterAvatar,
  inviteMessage,
  inviteNote,
}: CommunityRequestCardProps) {
  const navigate = useNavigate()
  const currentUserId = useAuthStore(s => s.user?.id)
  const isSelfCreator = currentUserId === ritualCreatorId
  const { acceptOrganizerInvite, isAccepting } = useAcceptOrganizerInvite(id)
  const { rejectOrganizerInvite, isRejecting } = useRejectOrganizerInvite(id)
  const createDmConversation = useCreateDmConversation()

  const isProcessing = isAccepting || isRejecting

  const formatDateTimeWithDuration = (dateStr: string | undefined, dur: string): string | null => {
    if (!dateStr) return null

    const date = new Date(dateStr)
    const month = date.toLocaleDateString("en-US", { month: "short" })
    const day = date.getDate()
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? "PM" : "AM"
    const h12 = hours % 12 || 12
    const timeStr = `${h12}:${minutes.toString().padStart(2, "0")} ${ampm}`

    return `${month} ${day}, ${timeStr} – ${dur} min`
  }

  const formattedDateTime = formatDateTimeWithDuration(dateTime, duration)

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation()
    acceptOrganizerInvite(organizerRecordId)
  }

  const handleDecline = (e: React.MouseEvent) => {
    e.stopPropagation()
    rejectOrganizerInvite(organizerRecordId)
  }

  return (
    <Card
      className="overflow-hidden h-full w-full border-0 bg-beige-medium p-0 cursor-pointer rounded-tl-[40px] rounded-tr-none rounded-bl-none flex flex-col gap-0 rounded-br-[40px] border border-white/15"
      onClick={() => navigate(`/rituals/${id}`)}
    >
      {/* Hero Image */}
      <div className="relative aspect-[647/300] w-full overflow-hidden rounded-br-[40px]">
        {image ? (
          <img
            src={image}
            alt={title}
            className="absolute inset-0 h-full w-full object-cover rounded-br-[40px] border border-b-0 border-[#F2F0EB1A]"
          />
        ) : (
          <div className="absolute inset-0 bg-charcoal/30 rounded-br-[40px] border border-b-0 border-[#F2F0EB1A]" />
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0.4)] via-[rgba(0,0,0,0.2)] to-[rgba(0,0,0,0.7)]" />

        {/* Chat Icon with badge */}
        {chatCount > 0 && (
          <div className="absolute top-5 right-5 relative p-3 rounded-lg bg-white/10 backdrop-blur-xs border border-white/15 ">
            <MessageCircleMore className="w-5 h-5 text-white" />
            <span className="absolute -top-2 -right-3 bg-brand-terracotta text-white text-[10px] font-medium rounded-full min-w-[25px] h-[25px] flex items-center justify-center px-1">
              {chatCount}
            </span>
          </div>
        )}
      </div>

      {/* Event Info */}
      <div className="flex flex-col gap-2.75 px-5 py-6.25 bg-[#F2F0EB0D] border border-t-0 border-[#F2F0EB1A]">
        <h3 className="line-clamp-2 text-[18px] font-semibold leading-5.5 text-primary">{title}</h3>

        <div className="flex flex-wrap items-center gap-2.5 text-[13px] leading-5 text-primary">
          <span
            className={cn(
              "rounded-[5px] px-1.5 py-0.5",
              participationFee === "Donation-based"
                ? "bg-brand-terracotta text-white"
                : participationFee === "Free"
                  ? "bg-olive text-white"
                  : "bg-[#C86757] text-white",
            )}
          >
            {participationFee === "Donation-based"
              ? "Donation-based"
              : participationFee === "Paid"
                ? `$${price}`
                : "Free"}
          </span>
          <span className=" rounded py-1">{type}</span>
          {formattedDateTime && (
            <span className="flex items-center gap-1">{formattedDateTime}</span>
          )}
          {location && (
            <span className="flex items-center gap-1 truncate overflow-hidden">@{location}</span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-[15px] px-5 py-6.25">
        {/* Invitation Section */}
        <div>
          <p className="text-[18px] font-semibold leading-5.5 tracking-[-0.72px] text-[#F2F0EBE6]">
            {inviteMessage}
          </p>
        </div>

        {/* Inviter Info */}
        <div className="flex items-center gap-2.5">
          <div className="w-7.5 h-7.5 rounded-full overflow-hidden bg-card flex-shrink-0">
            {inviterAvatar ? (
              <img src={inviterAvatar} alt={inviterName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-primary bg-primary text-sm">
                {inviterName[0]}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[#F2F0EBE6] text-sm leading-5.5">
                {inviterName}
              </span>
              {inviterUsername && (
                <span className="text-[#F2F0EB80] text-[13px]">@{inviterUsername}</span>
              )}
            </div>
          </div>
        </div>
        <div>
          {inviteNote && <p className="text-[#F2F0EBE6] text-[12px] line-clamp-2">{inviteNote}</p>}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={handleAccept}
              disabled={isProcessing}
              className="flex items-center justify-center gap-1 bg-[#F2F0EB] font-semibold rounded-full pl-7.75 w-30 h-[50px] pr-7.5 py-3.75 text-sm cursor-pointer hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-[#020202]"
            >
              ACCEPT
            </button>
            <button
              type="button"
              onClick={handleDecline}
              disabled={isProcessing}
              className="flex items-center justify-center gap-1 bg-transparent border border-[#F2F0EB] text-white font-semibold rounded-full w-30 h-[50px] pl-7 pr-6.75 py-3.75 text-sm cursor-pointer hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              DECLINE
            </button>
          </div>
          {/* Chat Button — defensive: hidden if the card somehow surfaces the
              user's own outgoing invite (would otherwise 400 from the
              backend's self-DM guard). */}
          <button
            type="button"
            onClick={e => {
              e.stopPropagation()
              createDmConversation.mutate(ritualCreatorId, {
                onSuccess: data => {
                  navigate(`/chats/dm/${data.id}`)
                },
              })
            }}
            disabled={isSelfCreator}
            hidden={isSelfCreator}
            className="relative flex items-center justify-center w-12.5 h-12.5 rounded-[10px] bg-transparent border border-[#F2F0EB26] text-primary hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer p-2.75"
          >
            <MessageCircleMore className="w-5 h-5" />
            {chatCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-brand-terracotta text-primary text-[10px] font-medium rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1">
                {chatCount > 99 ? "99+" : chatCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </Card>
  )
}
